import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import unifiedApi from '../utils/unifiedApi';

export const FEATURES = Object.freeze({
  SPARES: 'spares',
});

const emptyFlags = { organization: {}, leagues: [] };

/** Resolves a feature for an organization (no league) or a league: league override, else organization, else on. */
export const resolveFeature = (flags, feature, leagueId) => {
  if (leagueId != null) {
    const league = (flags?.leagues || []).find((l) => String(l.leagueId) === String(leagueId));
    const override = league?.overrides?.[feature];
    if (typeof override === 'boolean') return override;
  }
  const orgValue = flags?.organization?.[feature];
  return typeof orgValue === 'boolean' ? orgValue : true;
};

/** The league's explicit override (true/false), or null when it follows the organization. */
export const getLeagueOverride = (flags, feature, leagueId) => {
  const league = (flags?.leagues || []).find((l) => String(l.leagueId) === String(leagueId));
  const override = league?.overrides?.[feature];
  return typeof override === 'boolean' ? override : null;
};

// Without a provider every feature is on and treated as loaded.
const FeatureContext = createContext({
  flags: emptyFlags,
  loaded: true,
  isEnabled: () => true,
  refresh: async () => {},
  setFlags: () => {},
});

/** Loads the organization's feature switches once per app load (public endpoint, works signed out). */
export const FeatureProvider = ({ children }) => {
  const [flags, setFlagsState] = useState(emptyFlags);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await unifiedApi.getFeatures();
      setFlagsState(data || emptyFlags);
    } catch {
      // Fail open: keep every feature on rather than hiding parts of the app.
      setFlagsState(emptyFlags);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setFlags = useCallback((data) => setFlagsState(data || emptyFlags), []);
  const isEnabled = useCallback((feature, leagueId) => resolveFeature(flags, feature, leagueId), [flags]);

  const value = useMemo(() => ({ flags, loaded, isEnabled, refresh, setFlags }), [flags, loaded, isEnabled, refresh, setFlags]);

  return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
};

export const useFeatures = () => useContext(FeatureContext);

/** True when the feature is on for the league (or the organization when no league is given). */
export const useFeature = (feature, leagueId) => {
  const { isEnabled } = useFeatures();
  return isEnabled(feature, leagueId);
};
